import React from "react";
import cn from "classnames";

import { SpriteIcon } from "@litres-libs/sprite-icons";

import type { IModalLayoutProps } from "../types";
import styles from "../styles/Modal.module.scss";
import { Button } from "../../../button";
import { useLocale } from "../../../locale";

export const ModalLayout = ({
  onClose,
  isMobile,
  noGutters,
  customClass,
  customHeaderClass,
  width,
  e2eId,
  title,
  showCloseButton = true,
  closeIcon,
  children,
  primaryButton,
  secondaryButton,
  ariaLabel,
}: IModalLayoutProps) => {
  const { t } = useLocale();
  const handleClose = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      className={cn(styles.layout, customClass, {
        [styles.layout_mobile]: isMobile,
        [styles.layout_noGutters]: noGutters,
      })}
      style={{ width }}
      data-testid={e2eId}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={title || ariaLabel || t("Модальное окно")}
      aria-describedby="dialogDesc"
    >
      {(title || showCloseButton) && (
        <div className={cn(styles.content__header, customHeaderClass)}>
          {title && <h3 className={styles.content__title}>{title}</h3>}
          {showCloseButton && (
            <div
              className={styles.close}
              data-testid="modal__close--button"
              onClick={handleClose}
              onKeyDown={handleKeyDown}
              role="button"
              aria-label={t("Закрыть")}
              tabIndex={0}
            >
              {closeIcon ? (
                React.cloneElement(closeIcon)
              ) : (
                <SpriteIcon name="close" size={24} />
              )}
            </div>
          )}
        </div>
      )}
      <div
        id="dialogDesc"
        className={styles.content}
        data-testid="modalWindow--content"
      >
        {children}
      </div>
      {(primaryButton || secondaryButton) && (
        <div
          className={cn(styles.controls, {
            [styles.with_one_button]: !secondaryButton,
          })}
        >
          {primaryButton && (
            <Button
              mode="primary"
              size="medium"
              onClick={(e): void => {
                primaryButton?.onClick(e);
              }}
              fullWidth={isMobile}
              className={styles.button}
              text={primaryButton.label}
              secondaryText={primaryButton.secondaryLabel}
              loading={primaryButton.loading}
            />
          )}
          {secondaryButton && (
            <Button
              mode="secondary"
              size="medium"
              onClick={(e): void => {
                secondaryButton?.onClick(e);
              }}
              fullWidth={isMobile}
              className={styles.button}
              text={secondaryButton.label}
              secondaryText={secondaryButton.secondaryLabel}
              loading={secondaryButton.loading}
            />
          )}
        </div>
      )}
    </div>
  );
};
